function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable prefer-destructuring */
/* eslint-disable arrow-body-style */
/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable consistent-return */
/**
 * ProductModal.jsx
 * ─────────────────────────────────────────────────────────
 * MAIN MODAL CONTAINER
 *
 * Orchestrates all sub-components and manages all local state:
 *  - quantity
 *  - selectedCustomizations
 *  - productNote
 *  - loading / error state for cart mutation
 *  - fetching customizationGroups for the product via GraphQL
 *
 * Animation (Framer Motion v6):
 *   Backdrop  → fade in/out
 *   Modal card → fade + slide up from y:60 → y:0
 *
 * Layout (matches design):
 *   MOBILE  – fixed to viewport:
 *     • top section  (image / title / description) – sticky top
 *     • middle       (customizations + note)       – scrollable
 *     • bottom bar   (qty + CTA)                   – sticky bottom
 *   DESKTOP – centred card, max-width 560px, full layout visible
 */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { produce } from 'immer';
import { useAppDispatch } from '@components/common/context/app';
import { useLanguage } from '@components/common/locale/useLanguage';
import { _ } from '@evershop/evershop/src/lib/locale/translate';
import ModalProductInfo from './ModalProductInfo';
import ModalCustomizationSelector from './ModalCustomizationSelector';
import ModalNoteInput from './ModalNoteInput';
import ModalQuantityController from './ModalQuantityController';
import ModalCartAction from './ModalCartAction';
import { extractDescriptionText } from '../../utils/categoryHelpers';
import './ProductModal.scss';

/**
 * Build a GraphQL query string to fetch product customizations and sauce flag.
 */
var buildProductQuery = productId => "\n    query {\n      product(id: ".concat(productId, ") {\n        productId\n        nameAr\n        description\n        descriptionAr\n        category {\n          categoryId\n          haveSauces\n          description\n          descriptionAr\n        }\n        customizationGroups {\n          customizationGroupId\n          groupName\n          groupNameAr\n          groupDescription\n          groupDescriptionAr\n          isRequired\n          minSelections\n          maxSelections\n          displayOrder\n          options {\n            customizationOptionId\n            optionName\n            optionNameAr\n            priceAddition\n            displayOrder\n            optionImage\n          }\n        }\n      }\n    }\n  ");

/* ─── Framer Motion animation variants ─── */
var backdropVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.25
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};
var cardVariants = {
  hidden: {
    opacity: 0,
    y: 60
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: 40,
    transition: {
      duration: 0.2,
      ease: 'easeIn'
    }
  }
};
var isCustomizationGroupRequired = group => (group === null || group === void 0 ? void 0 : group.isRequired) === true || (group === null || group === void 0 ? void 0 : group.isRequired) === 'true' || (group === null || group === void 0 ? void 0 : group.isRequired) === 1 || (group === null || group === void 0 ? void 0 : group.isRequired) === '1';

/**
 * @param {object}   props
 * @param {boolean}  props.isOpen        - Whether the modal is visible
 * @param {object}   props.product       - Product data
 * @param {Function} props.onClose       - Called when user dismisses the modal
 * @param {string}   [props.storeStatus] - '0' = busy/closed
 */
export default function ProductModal(_ref) {
  var _product$price, _product$image;
  var {
    isOpen,
    product,
    onClose,
    storeStatus
  } = _ref;
  var {
    isAr
  } = useLanguage();
  /* ── Local state ── */
  var [quantity, setQuantity] = useState(1);
  var [productNote, setProductNote] = useState('');
  var [selectedCustomizations, setSelectedCustomizations] = useState({});
  var [customizationsLoading, setCustomizationsLoading] = useState(false);
  var [cartLoading, setCartLoading] = useState(false);
  var [customizationGroups, setCustomizationGroups] = useState([]);
  var [, setCartError] = useState(null);
  var [selectedSauces, setSelectedSauces] = useState([]);
  var [sauceProducts, setSauceProducts] = useState([]);
  var [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  var scrollRef = useRef(null);
  var [sauceCategory, setSauceCategory] = useState(null);
  /* ── App context ── */
  var {
    setData,
    fetchPageData
  } = useAppDispatch();

  /* ── Lock / unlock body scroll when modal opens or closes ── */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  /* ── Reset state each time the modal opens ── */
  useEffect(() => {
    if (isOpen && product) {
      setQuantity(1);
      setProductNote('');
      setSelectedSauces([]);
      setSelectedCustomizations({});
    }
  }, [isOpen, product === null || product === void 0 ? void 0 : product.productId]);

  /* ── Fetch customization groups whenever the product changes ── */
  useEffect(() => {
    if (!isOpen || !(product !== null && product !== void 0 && product.productId)) return;
    var cancelled = false;
    setCustomizationsLoading(true);
    setCustomizationGroups([]);
    setSauceProducts([]);
    fetch('/api/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query: buildProductQuery(product.productId)
      })
    }).then(r => r.json()).then(data => {
      if (!cancelled) {
        var _data$data, _productData$category;
        var productData = data === null || data === void 0 || (_data$data = data.data) === null || _data$data === void 0 ? void 0 : _data$data.product;
        setCustomizationGroups((productData === null || productData === void 0 ? void 0 : productData.customizationGroups) || []);
        if (productData !== null && productData !== void 0 && (_productData$category = productData.category) !== null && _productData$category !== void 0 && _productData$category.haveSauces) {
          fetch('/api/graphql', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              query: "\n                  query {\n                    categories(filters: [{key: \"urlKey\", operation: eq, value: \"Sauces\"}]) {\n                      items {\n                        description\n                        descriptionAr\n                        products {\n                          items {\n                            productId\n                            name\n                            nameAr\n                            image {\n                              thumb\n                              single\n                            }\n                            price {\n                              regular { value text }\n                              special { value text }\n                            }\n                          }\n                        }\n                      }\n                    }\n                  }\n                "
            })
          }).then(r => r.json()).then(sauceData => {
            if (!cancelled) {
              var _sauceData$data, _category$products;
              var category = sauceData === null || sauceData === void 0 || (_sauceData$data = sauceData.data) === null || _sauceData$data === void 0 || (_sauceData$data = _sauceData$data.categories) === null || _sauceData$data === void 0 || (_sauceData$data = _sauceData$data.items) === null || _sauceData$data === void 0 ? void 0 : _sauceData$data[0];
              var items = (category === null || category === void 0 || (_category$products = category.products) === null || _category$products === void 0 ? void 0 : _category$products.items) || [];
              setSauceProducts(items);
              setSauceCategory(category);
            }
          });
        }
      }
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.error('[ProductModal] Fetch error:', err);
    }).finally(() => {
      if (!cancelled) setCustomizationsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [product === null || product === void 0 ? void 0 : product.productId, isOpen]);

  /* ── Derived: base price ── */
  var basePrice = (_product$price = product === null || product === void 0 ? void 0 : product.price) !== null && _product$price !== void 0 ? _product$price : 0;

  /* ── Derived: unit price including customizations + sauces ── */
  var unitPrice = useMemo(() => {
    var price = basePrice;
    if (customizationGroups.length > 0) {
      customizationGroups.forEach(group => {
        var selectedIds = selectedCustomizations[group.customizationGroupId] || [];
        selectedIds.forEach(optId => {
          var _group$options;
          var matchedOption = (_group$options = group.options) === null || _group$options === void 0 ? void 0 : _group$options.find(o => String(o.customizationOptionId) === String(optId));
          if (matchedOption !== null && matchedOption !== void 0 && matchedOption.priceAddition) {
            price += parseFloat(matchedOption.priceAddition);
          }
        });
      });
    }
    selectedSauces.forEach(sauceId => {
      var _sauce$price, _sauce$price2;
      var sauce = sauceProducts.find(s => String(s.productId) === String(sauceId));
      var saucePrice = (sauce === null || sauce === void 0 || (_sauce$price = sauce.price) === null || _sauce$price === void 0 || (_sauce$price = _sauce$price.special) === null || _sauce$price === void 0 ? void 0 : _sauce$price.value) || (sauce === null || sauce === void 0 || (_sauce$price2 = sauce.price) === null || _sauce$price2 === void 0 || (_sauce$price2 = _sauce$price2.regular) === null || _sauce$price2 === void 0 ? void 0 : _sauce$price2.value) || 0;
      price += saucePrice;
    });
    return price;
  }, [basePrice, selectedSauces, sauceProducts, customizationGroups, selectedCustomizations]);
  var totalPrice = unitPrice * quantity;

  /* ── Derived: are all required customization options selected? ── */
  var allRequiredSelected = useMemo(() => {
    return customizationGroups.filter(group => isCustomizationGroupRequired(group) && Number(group.minSelections) > 0).every(group => {
      var count = (selectedCustomizations[group.customizationGroupId] || []).length;
      return count >= Number(group.minSelections);
    });
  }, [customizationGroups, selectedCustomizations]);
  var handleCustomizationChange = useCallback((groupId, optionId, isSelected) => {
    setSelectedCustomizations(prev => {
      var current = prev[groupId] || [];
      var group = customizationGroups.find(g => g.customizationGroupId === groupId);
      var isSingleSelect = Number(group === null || group === void 0 ? void 0 : group.maxSelections) === 1;
      if (isSelected) {
        if (isSingleSelect) return _objectSpread(_objectSpread({}, prev), {}, {
          [groupId]: [optionId]
        });
        return _objectSpread(_objectSpread({}, prev), {}, {
          [groupId]: [...current, optionId]
        });
      }
      return _objectSpread(_objectSpread({}, prev), {}, {
        [groupId]: current.filter(id => id !== optionId)
      });
    });
  }, [customizationGroups]);

  /* ── Sauce selection handler ── */
  var handleSauceToggle = useCallback(sauceId => {
    setSelectedSauces(prev => {
      if (prev.includes(sauceId)) {
        return prev.filter(id => id !== sauceId);
      }
      return [...prev, sauceId];
    });
  }, []);

  /* ── Quantity handlers ── */
  var handleIncrease = useCallback(() => setQuantity(q => q + 1), []);
  var handleDecrease = useCallback(() => setQuantity(q => Math.max(1, q - 1)), []);

  /* ── Add-to-cart handler ── */
  var handleAddToCart = useCallback(async () => {
    if (storeStatus === '0') return;
    setCartLoading(true);
    setCartError(null);
    try {
      var customizationOptionsArray = [];
      Object.entries(selectedCustomizations).forEach(_ref2 => {
        var [groupId, optionIds] = _ref2;
        var group = customizationGroups.find(g => String(g.customizationGroupId) === String(groupId));
        if (group) {
          optionIds.forEach(optId => {
            var opt = group.options.find(o => String(o.customizationOptionId) === String(optId));
            if (opt) {
              var _group$displayOrder;
              customizationOptionsArray.push({
                group_id: groupId,
                group_name: group.groupName,
                group_display_order: (_group$displayOrder = group.displayOrder) !== null && _group$displayOrder !== void 0 ? _group$displayOrder : 0,
                option_id: optId,
                option_name: opt.optionName,
                price_addition: opt.priceAddition
              });
            }
          });
        }
      });
      var body = {
        product_id: product.productId,
        qty: quantity,
        name: product.name,
        product_side: '[]',
        product_note: JSON.stringify({
          productName: product.name,
          note: productNote || '',
          sauces: selectedSauces.map(id => {
            var _s$price, _s$price2;
            var s = sauceProducts.find(p => String(p.productId) === String(id));
            return {
              name: s === null || s === void 0 ? void 0 : s.name,
              nameAr: (s === null || s === void 0 ? void 0 : s.nameAr) || (s === null || s === void 0 ? void 0 : s.name),
              price: (s === null || s === void 0 || (_s$price = s.price) === null || _s$price === void 0 || (_s$price = _s$price.special) === null || _s$price === void 0 ? void 0 : _s$price.value) || (s === null || s === void 0 || (_s$price2 = s.price) === null || _s$price2 === void 0 || (_s$price2 = _s$price2.regular) === null || _s$price2 === void 0 ? void 0 : _s$price2.value) || 0
            };
          }),
          timestamp: new Date().toISOString()
        }),
        product_price: String(unitPrice),
        customization_options: customizationOptionsArray
      };
      var response = await fetch('/api/cart/mine/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      var result = await response.json();
      if (!result.error) {
        try {
          localStorage.setItem('cartId', result.data.cartId);
        } catch (_) {
          // Private-browse / storage quota — silently ignore
        }
        setData(currentData => produce(currentData || {}, draft => {
          draft.cart = draft.cart || {};
          draft.cart.totalQty = (Number(draft.cart.totalQty) || 0) + quantity;
          draft.cart.uuid = result.data.cartId;
        }));
        if (window.location.pathname === '/cart') {
          var url = new URL(window.location.href);
          url.searchParams.set('ajax', true);
          await fetchPageData(url);
        }
        onClose();
      } else {
        setCartError(result.error.message);
      }
    } catch (err) {
      setCartError(err.message);
    } finally {
      setCartLoading(false);
    }
  }, [product, quantity, productNote, selectedCustomizations, customizationGroups, setData, storeStatus, onClose, selectedSauces, sauceProducts, unitPrice]);

  /* ── Keyboard close (Escape) ── */
  useEffect(() => {
    var handleKeyDown = e => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  /* ── Track scroll position to toggle header collapse state ── */
  var handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    var scrollTop = scrollRef.current.scrollTop;
    setIsHeaderCollapsed(scrollTop > 80);
  }, []);

  /* ── Render ── */
  return /*#__PURE__*/React.createElement(AnimatePresence, null, isOpen && product && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "modal-root-wrapper"
  }, /*#__PURE__*/React.createElement(motion.div, {
    className: "modal-backdrop",
    variants: backdropVariants,
    initial: "hidden",
    animate: "visible",
    exit: "exit",
    onClick: onClose,
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement(motion.div, {
    className: "modal-card",
    variants: cardVariants,
    initial: "hidden",
    animate: "visible",
    exit: "exit",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "".concat(product.name, " details")
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "modal-close-btn ".concat(isHeaderCollapsed ? 'modal-close-btn--dark' : ''),
    onClick: onClose,
    "aria-label": "Close modal"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "22",
    height: "22",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("line", {
    x1: "18",
    y1: "6",
    x2: "6",
    y2: "18"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "6",
    y1: "6",
    x2: "18",
    y2: "18"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "modal-scroll-area",
    ref: scrollRef,
    onScroll: handleScroll
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-top"
  }, /*#__PURE__*/React.createElement(ModalProductInfo, {
    image: ((_product$image = product.image) === null || _product$image === void 0 ? void 0 : _product$image.single) || product.image,
    name: product.name
  })), /*#__PURE__*/React.createElement("div", {
    className: "modal-product-text"
  }, /*#__PURE__*/React.createElement("span", {
    className: "modal-product-name"
  }, isAr ? product.nameAr || product.name : product.name), (product.description || product.descriptionAr) && /*#__PURE__*/React.createElement("span", {
    className: "modal-product-description rtl:mt-[0.8rem] rtl:md:mt-[1.2rem]"
  }, extractDescriptionText(isAr ? product.descriptionAr || product.description : product.description))), /*#__PURE__*/React.createElement("div", {
    className: "modal-middle"
  }, customizationsLoading ? /*#__PURE__*/React.createElement("div", {
    className: "modal-variants-loading"
  }, /*#__PURE__*/React.createElement("span", {
    className: "modal-cart-spinner"
  })) : /*#__PURE__*/React.createElement(ModalCustomizationSelector, {
    customizationGroups: customizationGroups,
    selectedCustomizations: selectedCustomizations,
    onCustomizationChange: handleCustomizationChange
  }), !customizationsLoading && sauceProducts.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "modal-sauces-section"
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-variant-header"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "modal-sauces-title"
  }, _('Make it Saucy')), /*#__PURE__*/React.createElement("span", {
    className: "modal-variant-optional-badge"
  }, _('Optional'))), /*#__PURE__*/React.createElement("p", {
    className: "modal-variant-subtitle"
  }, isAr ? (sauceCategory === null || sauceCategory === void 0 ? void 0 : sauceCategory.descriptionAr) || (sauceCategory === null || sauceCategory === void 0 ? void 0 : sauceCategory.description) || _('Choose more than 1') : (sauceCategory === null || sauceCategory === void 0 ? void 0 : sauceCategory.description) || _('Choose more than 1')), /*#__PURE__*/React.createElement("div", {
    className: "modal-sauce-grid"
  }, sauceProducts.map(sauce => {
    var _sauce$price3, _sauce$price4, _sauce$image, _sauce$image2;
    var isSelected = selectedSauces.includes(sauce.productId);
    var price = ((_sauce$price3 = sauce.price) === null || _sauce$price3 === void 0 || (_sauce$price3 = _sauce$price3.special) === null || _sauce$price3 === void 0 ? void 0 : _sauce$price3.value) || ((_sauce$price4 = sauce.price) === null || _sauce$price4 === void 0 || (_sauce$price4 = _sauce$price4.regular) === null || _sauce$price4 === void 0 ? void 0 : _sauce$price4.value);
    return /*#__PURE__*/React.createElement("div", {
      key: sauce.productId,
      role: "button",
      tabIndex: 0,
      className: "modal-sauce-card ".concat(isSelected ? 'active' : ''),
      onClick: () => handleSauceToggle(sauce.productId),
      onKeyDown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSauceToggle(sauce.productId);
        }
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-sauce-image-wrapper"
    }, /*#__PURE__*/React.createElement("img", {
      src: ((_sauce$image = sauce.image) === null || _sauce$image === void 0 ? void 0 : _sauce$image.single) || ((_sauce$image2 = sauce.image) === null || _sauce$image2 === void 0 ? void 0 : _sauce$image2.thumb),
      alt: sauce.name,
      className: "modal-sauce-image"
    })), /*#__PURE__*/React.createElement("div", {
      className: "modal-sauce-content"
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-sauce-checkbox"
    }, isSelected && /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "white",
      strokeWidth: "4",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("polyline", {
      points: "20 6 9 17 4 12"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "modal-sauce-details"
    }, /*#__PURE__*/React.createElement("span", {
      className: "modal-sauce-name ".concat(isAr ? '!text-right' : '')
    }, isAr ? sauce.nameAr || sauce.name : sauce.name), /*#__PURE__*/React.createElement("span", {
      className: "modal-sauce-price ".concat(isAr ? '!font-rubik !text-right ' : '')
    }, "+ ", isAr ? "".concat(price, " \u062C\u0645") : "EGP ".concat(price)))));
  }))), /*#__PURE__*/React.createElement("div", {
    className: "md:px-[4.5rem]"
  }, /*#__PURE__*/React.createElement(ModalNoteInput, {
    value: productNote,
    onChange: setProductNote
  })))), /*#__PURE__*/React.createElement("div", {
    className: "modal-bottom"
  }, /*#__PURE__*/React.createElement(ModalQuantityController, {
    quantity: quantity,
    onIncrease: handleIncrease,
    onDecrease: handleDecrease
  }), /*#__PURE__*/React.createElement(ModalCartAction, {
    totalPrice: totalPrice,
    currency: product.currency || 'EGP',
    isLoading: customizationsLoading || cartLoading,
    onAddToCart: handleAddToCart,
    disabled: storeStatus === '0' || !allRequiredSelected
  }))))));
}